import type { CounterpartAppointmentType } from '@/types/appointmentType';
import type {
  ChronologicalScheduleExpectation,
  ScheduleExpectation
} from '@/types/scheduleExpectation';

export type SwitchDurationAvailability =
  | { status: 'hidden' }
  | { status: 'disabled'; reason: string; pendingStartAt?: string }
  | { status: 'enabled' };

export const getSwitchDurationAvailability = ({
  currentExpectation,
  counterpart,
  counterpartNodes,
  now = new Date()
}: {
  currentExpectation: ScheduleExpectation | null | undefined;
  counterpart: CounterpartAppointmentType | null | undefined;
  counterpartNodes: ChronologicalScheduleExpectation[] | undefined;
  now?: Date;
}): SwitchDurationAvailability => {
  if (!counterpart) return { status: 'hidden' };
  if (!currentExpectation || currentExpectation.frequency <= 0) {
    return { status: 'hidden' };
  }

  // A sequence drives a schedule of its own, so swapping one leg of it for the
  // counterpart type would leave the rest of the sequence behind. Only
  // expectations in effect right now are checked -- a sequence starting later
  // is out of scope (AP-8903).
  if (currentExpectation.sequence_id) {
    return {
      status: 'disabled',
      reason: 'Cannot change duration because the current expectation is part of a sequence.'
    };
  }

  const counterpartExpectation = counterpart.current_expectation;
  if (counterpartExpectation?.sequence_id) {
    return {
      status: 'disabled',
      reason: `Cannot change duration because the ${counterpart.appointment_type_name} expectation is part of a sequence.`
    };
  }

  if ((counterpartExpectation?.frequency ?? 0) > 0) {
    return {
      status: 'disabled',
      reason: `Cannot change duration because ${counterpart.appointment_type_name} is already expected.`
    };
  }

  // currentExpectation only reflects what applies right now, not a change
  // already scheduled for a future date.
  const futureNode = (counterpartNodes ?? []).find(
    (node) => !!node.start_at && new Date(node.start_at) > now
  );
  if (futureNode) {
    return {
      status: 'disabled',
      reason: 'Cannot change duration because an expectation change is already pending.',
      pendingStartAt: futureNode.start_at as string
    };
  }

  return { status: 'enabled' };
};
